/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { validationUniqueSymbolsPassword } from '@/auth/lib/validationUniqueSymbolsPassword';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ValidateLengthConfigProps, statusTypes } from '@/auth/types';
import { useText } from '@adobe-commerce/elsie/i18n';

interface UsePasswordValidationMessageProps {
  passwordConfigs: {
    minLength: number;
    requiredCharacterClasses: number;
  } | null;
  isClickSubmit: boolean;
  password: string;
}

export const usePasswordValidationMessage = ({
  passwordConfigs,
  isClickSubmit,
  password,
}: UsePasswordValidationMessageProps) => {
  const translations = useText({
    messageLengthPassword:
      'Auth.PasswordValidationMessage.messageLengthPassword',
  });

  const [isValidUniqueSymbols, setIsValidUniqueSymbols] =
    useState<statusTypes>('pending');

  useEffect(() => {
    if (!passwordConfigs) return;

    const result = validationUniqueSymbolsPassword(
      password,
      passwordConfigs.requiredCharacterClasses
    );

    if (isClickSubmit && password.length > 0) {
      result
        ? setIsValidUniqueSymbols('success')
        : setIsValidUniqueSymbols('error');
    } else if (isClickSubmit && password.length === 0) {
      setIsValidUniqueSymbols('pending');
    } else {
      result
        ? setIsValidUniqueSymbols('success')
        : setIsValidUniqueSymbols('pending');
    }
  }, [isClickSubmit, passwordConfigs, password]);

  const defaultLengthMessage: ValidateLengthConfigProps | undefined =
    useMemo(() => {
      if (!passwordConfigs) return;

      const defaultMessage: ValidateLengthConfigProps = {
        status: 'pending',
        icon: 'pending',
        message: translations.messageLengthPassword?.replace(
          '{minLength}',
          `${passwordConfigs.minLength}`
        ),
      };

      if (password.length && password.length >= passwordConfigs.minLength) {
        return { ...defaultMessage, icon: 'success', status: 'success' };
      }

      if (password.length && password.length < passwordConfigs.minLength) {
        return isClickSubmit
          ? { ...defaultMessage, icon: 'error', status: 'error' }
          : { ...defaultMessage, icon: 'pending', status: 'pending' };
      }

      return defaultMessage;
    }, [
      passwordConfigs,
      translations.messageLengthPassword,
      password?.length,
      isClickSubmit,
    ]);

  return { isValidUniqueSymbols, defaultLengthMessage };
};
